import {
  Chart,
  BarController,
  BarElement,
  ArcElement,
  PieController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

Chart.register(
  BarController,
  BarElement,
  ArcElement,
  PieController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

// ===== 棒グラフ =====
const barValueLabelPlugin = {
  id: 'barValueLabelPlugin',
  afterDatasetsDraw(chart, args, pluginOptions) {
    const { ctx } = chart;

    const datasetIndex = pluginOptions?.datasetIndex ?? 0;
    const dataset = chart.data.datasets[datasetIndex];
    if (!dataset) return;

    const meta = chart.getDatasetMeta(datasetIndex);
    if (!meta || meta.hidden) return;

    // animation.onProgress で保存した進行率（0〜1）
    const p = chart.$valueProgress ?? 1;

    // ===== オプション（デフォルト）=====
    const suffix = pluginOptions?.suffix ?? '名';
    const offsetY = pluginOptions?.offsetY ?? 8;

    // 数字側
    const numberFontSize = pluginOptions?.numberFontSize ?? 38;
    const numberFontWeight = pluginOptions?.numberFontWeight ?? 900;
    const numberItalic = pluginOptions?.numberItalic ?? true;

    // 「名」側
    const unitFontSize = pluginOptions?.unitFontSize ?? 18;
    const unitFontWeight = pluginOptions?.unitFontWeight ?? 600;
    const unitMarginLeft = pluginOptions?.unitMarginLeft ?? 6;

    // 数字にだけ left offset（例：-20）
    const numberOffsetX = pluginOptions?.numberOffsetX ?? 0;

    const fontFamily = pluginOptions?.fontFamily ?? '"Zen Kaku Gothic New", sans-serif';
    const color = pluginOptions?.color ?? '#333';

    ctx.save();
    ctx.fillStyle = color;
    ctx.textBaseline = 'bottom';
    ctx.textAlign = 'left'; // ★ 左寄せで描く（合計幅で中央寄せする）

    meta.data.forEach((bar, i) => {
      const raw = dataset.data[i];
      const value = typeof raw === 'number' ? raw : parseFloat(raw);
      if (Number.isNaN(value)) return;

      const current = Math.round(value * p);
      const numberText = `${current}`;

      const { x, y } = bar.tooltipPosition();

      // ---- 幅を計測（数字と名でフォントが違うので両方測る）----
      ctx.font = `${
        numberItalic ? 'italic ' : ''
      }${numberFontWeight} ${numberFontSize}px ${fontFamily}`;
      const numberWidth = ctx.measureText(numberText).width;

      ctx.font = `${unitFontWeight} ${unitFontSize}px ${fontFamily}`;
      const unitWidth = ctx.measureText(suffix).width;

      const totalWidth = numberWidth + unitMarginLeft + unitWidth;

      // ★ 合計幅がバー中央に来るように開始Xを決める + 数字の left offset
      const startX = x - totalWidth / 2 + numberOffsetX;
      const drawY = y - offsetY;

      // ---- 数字を描画（italic / weight / size）----
      ctx.font = `${
        numberItalic ? 'italic ' : ''
      }${numberFontWeight} ${numberFontSize}px ${fontFamily}`;
      ctx.fillText(numberText, startX, drawY);

      // ---- 「名」を描画（別 weight / size + margin-left）----
      ctx.font = `${unitFontWeight} ${unitFontSize}px ${fontFamily}`;
      ctx.fillText(suffix, startX + numberWidth + unitMarginLeft, drawY);
    });

    ctx.restore();
  },
};

export const initBarChart = () => {
  const el = document.getElementById('barChart');
  if (!el) return;

  // 1) まずは一回だけ生成（ここでは animation: false でOK）
  const chart = new Chart(el, {
    type: 'bar',
    data: {
      labels: ['関東', '関西', '九州', 'その他'],
      datasets: [
        {
          data: [80, 10, 5, 10],
          backgroundColor: '#e69138',
          borderRadius: 8,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,

      // ★ ここは「update()したときに効くアニメ設定」
      animation: {
        duration: 900,
        easing: 'easeOutQuart',
        onProgress: (anim) => {
          const p = anim?.currentStep && anim?.numSteps ? anim.currentStep / anim.numSteps : 1;
          anim.chart.$valueProgress = p;
        },
        onComplete: (anim) => {
          anim.chart.$valueProgress = 1;
        },
      },

      // ★ 初期生成時はアニメしない（スクロール時にだけ動かす）
      // （Chart.js的には "update('none')" 相当の意味）
      // ただし options.animation 自体は残しておく
      // -> onEnter で reset + update するのでOK

      transitions: {
        active: { animation: { duration: 0 } },
      },

      plugins: {
        legend: { display: false },
        tooltip: { enabled: true, animation: false },
        barValueLabelPlugin: {
          suffix: '名',
          offsetY: 8,

          // 数字
          numberFontSize: 38,
          numberFontWeight: 900,
          numberItalic: true,
          numberOffsetX: 0,

          // 単位
          unitFontSize: 24,
          unitFontWeight: 400,
          unitMarginLeft: 10,
        },
      },

      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { font: { size: 18, weight: 700 } },
        },
        y: {
          beginAtZero: true,
          grid: { display: false },
          border: { display: false },
          ticks: { display: false },
        },
      },
      layout: {
        padding: {
          top: 50,
        },
      },
    },
    plugins: [barValueLabelPlugin],
  });

  // ★ 初期状態を「アニメ前」に戻しておく（生成直後に最終状態になるのを防ぐ）
  chart.$valueProgress = 0;
  chart.update('none'); // まず描画だけ（アニメ無し）

  // 2) スクロールで入ったら “確実に” アニメさせる
  ScrollTrigger.create({
    trigger: el,
    start: 'top 80%',
    onEnter: () => {
      chart.$valueProgress = 0;
      chart.reset(); // ← 重要：初期状態へ
      chart.update(); // ← 重要：アニメ付きで更新
    },
    onEnterBack: () => {
      chart.$valueProgress = 0;
      chart.reset();
      chart.update();
    },
    // markers: true,
  });

  // レイアウト確定後にズレる場合の保険
  ScrollTrigger.refresh();
};

// ===== 円グラフ =====
const pieValueLabelPlugin = {
  id: 'pieValueLabelPlugin',
  afterDatasetsDraw(chart, args, opts) {
    const { ctx } = chart;
    const datasetIndex = opts?.datasetIndex ?? 0;
    const meta = chart.getDatasetMeta(datasetIndex);
    const dataset = chart.data.datasets?.[datasetIndex];
    const labels = chart.data.labels ?? [];

    if (!meta || !dataset || meta.hidden) return;

    const p = chart.$valueProgress ?? 1;

    // ===== 見た目オプション =====
    const fontFamily = opts?.fontFamily ?? '"Zen Kaku Gothic New", sans-serif';
    const color = opts?.color ?? '#333';

    // ★ 追加：スライスごとの文字色（[経験者, 未経験]）
    const colors = opts?.colors ?? [color, color];

    const numSize = opts?.numberFontSize ?? 48;
    const numWeight = opts?.numberFontWeight ?? 900;
    const numItalic = opts?.numberItalic ?? true;

    const pctSize = opts?.percentFontSize ?? 24;
    const pctWeight = opts?.percentFontWeight ?? 400;

    const labelSize = opts?.labelFontSize ?? 18;
    const labelWeight = opts?.labelFontWeight ?? 700;

    const baseDx = opts?.dx ?? 0;
    const baseDy = opts?.dy ?? 0;
    const rMul = opts?.radiusMultiplier ?? 1.12;
    const gapY = opts?.gapY ?? 8;
    const percentGap = opts?.percentGap ?? 6;

    const offsets = opts?.offsets ?? [];

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    meta.data.forEach((arc, i) => {
      const raw = dataset.data[i];
      const target = typeof raw === 'number' ? raw : parseFloat(raw);
      if (Number.isNaN(target)) return;

      const current = Math.round(target * p);

      const midAngle = (arc.startAngle + arc.endAngle) / 2;
      const r = (arc.outerRadius || 0) * rMul;
      const x = arc.x + Math.cos(midAngle) * r;
      const y = arc.y + Math.sin(midAngle) * r;

      const odx = offsets[i]?.dx ?? 0;
      const ody = offsets[i]?.dy ?? 0;

      const drawX = x + baseDx + odx;
      const drawY = y + baseDy + ody;

      // ★ 追加：未経験だけ色を変える（i=1が未経験）
      ctx.fillStyle = colors[i] ?? color;

      // ===== 1) 数字（大） =====
      const numText = `${current}`;
      ctx.font = `${numItalic ? 'italic ' : ''}${numWeight} ${numSize}px ${fontFamily}`;
      ctx.fillText(numText, drawX, drawY);

      const numWidth = ctx.measureText(numText).width;

      // ===== 2) %（小） =====
      ctx.font = `${pctWeight} ${pctSize}px ${fontFamily}`;
      ctx.fillText('%', drawX + numWidth / 2 + percentGap + 10, drawY + 8);

      // ===== 3) ラベル（下） =====
      const label = labels[i] ?? '';
      ctx.font = `${labelWeight} ${labelSize}px ${fontFamily}`;
      ctx.fillText(label, drawX, drawY + numSize * 0.55 + gapY);
    });

    ctx.restore();
  },
};

export const initPieChart = () => {
  const el = document.getElementById('pieChart');
  if (!el) return;

  const chart = new Chart(el, {
    type: 'pie',
    data: {
      labels: ['経験者', '未経験'],
      datasets: [
        {
          data: [70, 30],
          backgroundColor: ['#e69138', '#707070'],
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      animation: {
        duration: 900,
        easing: 'easeOutQuart',
        onProgress: (anim) => {
          const p = anim?.currentStep && anim?.numSteps ? anim.currentStep / anim.numSteps : 1;
          anim.chart.$valueProgress = p;
        },
        onComplete: (anim) => {
          anim.chart.$valueProgress = 1;
        },
      },
      transitions: {
        active: { animation: { duration: 0 } }, // hoverで再アニメしない
      },
      plugins: {
        legend: { display: false }, // ★ 下の凡例は不要
        tooltip: { enabled: true, animation: false },

        pieValueLabelPlugin: {
          // 全体の位置調整（position/margin感覚）
          dx: 0,
          dy: 0,

          // 各ラベルの個別微調整
          offsets: [
            { dx: -70, dy: -76 }, // 経験者
            { dx: 42, dy: 36 }, // 未経験
          ],

          radiusMultiplier: 1.12, // 外側へ出す量
          numberFontSize: 48,
          numberFontWeight: 900,
          numberItalic: true,

          percentFontSize: 24, // ★ %だけ小さく
          percentFontWeight: 400, // ★ %だけ軽く

          labelFontSize: 18,
          labelFontWeight: 700,

          percentGap: 6,
          gapY: 6,

          colors: ['#fff', '#fff'],
        },
      },
      layout: {
        padding: {
          bottom: 50,
        },
      },
    },
    plugins: [pieValueLabelPlugin],
  });

  // 初期は0表示に
  chart.$valueProgress = 0;
  chart.update('none');

  // ScrollTriggerで入ったらアニメ
  ScrollTrigger.create({
    trigger: el,
    start: 'top 80%',
    onEnter: () => {
      chart.$valueProgress = 0;
      chart.reset();
      chart.update();
    },
    onEnterBack: () => {
      chart.$valueProgress = 0;
      chart.reset();
      chart.update();
    },
  });
};

export const initPieChartMaleToFemaleRatio = () => {
  const el = document.getElementById('pieChartMaleToFemaleRatio');
  if (!el) return;

  const chart = new Chart(el, {
    type: 'pie',
    data: {
      labels: ['男', '女'],
      datasets: [
        {
          data: [59, 41],
          backgroundColor: ['#707070', '#a0a0a0'],
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      animation: {
        duration: 900,
        easing: 'easeOutQuart',
        onProgress: (anim) => {
          const p = anim?.currentStep && anim?.numSteps ? anim.currentStep / anim.numSteps : 1;
          anim.chart.$valueProgress = p;
        },
        onComplete: (anim) => {
          anim.chart.$valueProgress = 1;
        },
      },
      transitions: {
        active: { animation: { duration: 0 } }, // hoverで再アニメしない
      },
      plugins: {
        legend: { display: false }, // ★ 下の凡例は不要
        tooltip: { enabled: true, animation: false },

        pieValueLabelPlugin: {
          // 全体の位置調整（position/margin感覚）
          dx: 0,
          dy: 0,

          // 各ラベルの個別微調整
          offsets: [
            { dx: -90, dy: -46 }, // 男
            { dx: 72, dy: 0 }, // 女
          ],

          radiusMultiplier: 1.12, // 外側へ出す量
          numberFontSize: 48,
          numberFontWeight: 900,
          numberItalic: true,

          percentFontSize: 24, // ★ %だけ小さく
          percentFontWeight: 400, // ★ %だけ軽く

          labelFontSize: 18,
          labelFontWeight: 700,

          percentGap: 6,
          gapY: 6,

          colors: ['#fff', '#fff'],
        },
      },
      layout: {
        padding: {
          bottom: 50,
        },
      },
    },
    plugins: [pieValueLabelPlugin],
  });

  // 初期は0表示に
  chart.$valueProgress = 0;
  chart.update('none');

  // ScrollTriggerで入ったらアニメ
  ScrollTrigger.create({
    trigger: el,
    start: 'top 80%',
    onEnter: () => {
      chart.$valueProgress = 0;
      chart.reset();
      chart.update();
    },
    onEnterBack: () => {
      chart.$valueProgress = 0;
      chart.reset();
      chart.update();
    },
  });
};

export default {
  initBarChart,
  initPieChart,
  initPieChartMaleToFemaleRatio,
};
